import {defer, shareReplay} from 'rxjs';
import {PATHS} from '@/const/paths';
import {stringify} from 'query-string';
import {web3Api} from '@/api/web3';
import {sd} from '@/da';
import {getFixedLinkUrl} from '@/helper/url';
import {popupUtil} from '@/helper/popup';
import {numUtil, toFixed} from '@/helper/num';
import {timeUtil} from '@/helper/time';
import SnsShareDialog from '@/widgets/SnsShareDialog.svelte';
import lang from './SocialTradePage.lang';

export function getWeekEndDate() {
  const now = new Date();
  const day = now.getUTCDay() || 7;
  now.setUTCDate(7 - day + now.getUTCDate());
  now.setUTCHours(23, 59, 59, 999);
  return now;
}

export function getWeekStartDate() {
  const now = new Date();
  const day = now.getUTCDay() || 7;
  now.setUTCDate(1 - day + now.getUTCDate());
  now.setUTCHours(0, 0, 0, 0);
  return now;
}

/**
 * 0代表周日
 */
export function getDayOfWeek(timestamp) {
  if (!timestamp) {
    return null;
  }
  const date = new Date(timestamp);
  const dayOfWeek = date.getUTCDay();
  return dayOfWeek;
}

export function getCurrentWeekDay() {
  const date = new Date();
  const dayOfWeek = date.getUTCDay() || 7;
  return dayOfWeek;
}

export function getAuthRedirectUri() {
  return window.location.origin + PATHS.REDIRECT.pathname;
}

export function setMockTwitterHeader(value) {
  localStorage.setItem('x-phemex-twitter-header-mock', value);
}

export function getMockTwitterHeader() {
  return localStorage.getItem('x-phemex-twitter-header-mock');
}

export function clearMockTwitterHeader() {
  localStorage.removeItem('x-phemex-twitter-header-mock');
}

export function getTempTwitterCode() {
  const value = localStorage.getItem('x-phemex-twitter-temp-code');
  if (value) {
    return JSON.parse(value);
  }
  return null;
}

export function clearTempTwitterCode() {
  localStorage.removeItem('x-phemex-twitter-temp-code');
}

export function setTempTwitterCode(value) {
  localStorage.setItem('x-phemex-twitter-temp-code', JSON.stringify(value));
}

export async function getShareLink({groupId}: {groupId: string}) {
  const {data} = await web3Api.proShareInfo({groupId});
  const pulseInviteFriendOriginUrl = location.origin + getFixedLinkUrl(PATHS.WEB3_SOCIAL_TRADE_INVITE.pathname);
  if (data) {
    if (data.referralCode) {
      return `${pulseInviteFriendOriginUrl}?${stringify({referralCode: encodeURIComponent(data.referralCode), group: data.group})}`;
    }
    return `${pulseInviteFriendOriginUrl}?${stringify({groupID: encodeURIComponent(data.groupId), memberId: encodeURIComponent(data.memberId)})}`;
  }
  return pulseInviteFriendOriginUrl;
}

export function formatPtValue(rv) {
  return numUtil.delimit(Number(numUtil.floor(rv, 2)));
}

/**
 * 唤起群组分享弹窗
 */
export async function openGroupSharedDialog(params: {LG: any; groupId: string; groupName: string; groupIcon: string; tgShare: string}) {
  const {LG, groupId, groupName = '', groupIcon: img, tgShare} = params;
  const cacheKey = '__shareLink';
  const cacheDatas = openGroupSharedDialog[cacheKey];
  const cacheLink = cacheDatas && cacheDatas.get(groupId);
  let link = '';
  if (cacheLink) {
    link = cacheLink;
  } else {
    link = await getShareLink({groupId});
    if (!cacheDatas) {
      openGroupSharedDialog[cacheKey] = new Map();
    }
    openGroupSharedDialog[cacheKey].set(groupId, link);
  }
  const text = `${LG(lang.InviteContent, {GroupName: groupName})}:`;
  return popupUtil.create(SnsShareDialog, {LG, img, link, text, tgShare});
}

export function getTwitterLink(name) {
  return `https://twitter.com/${name}`;
}

export function formatRewardValue(value) {
  try {
    const {delimit, unAbbreviateNumber} = numUtil;
    const validNumValue = unAbbreviateNumber(delimit(value));
    return toFixed(validNumValue, 2);
  } catch (error) {
    sd.track('rewards_delimit_error', {value});
    return 0;
  }
}

export function formatRangeAmountText({min, max}) {
  return max && max != '0' ? `${numUtil.delimit(min || 0)} - ${numUtil.delimit(max || 0)}` : '0';
}

export async function readImageFileMetaData(file: File): Promise<{width: number; height: number; objectUrl: string}> {
  try {
    const image = new Image();
    const objectUrl = URL.createObjectURL(file);
    image.src = objectUrl;
    const result = await new Promise<{width: number; height: number; objectUrl: string}>((resolve, reject) => {
      image.onload = () => {
        resolve({width: image.width, height: image.height, objectUrl});
      };
      image.onerror = reject;
    });
    return result;
  } catch (error) {}
}

export function formatTimestamp(createTime) {
  if (!createTime) {
    return '';
  }
  try {
    const timestamp = new Date(createTime).getTime();
    const now = Date.now();
    const diffInSeconds = Math.floor((now - timestamp) / 1000);
    if (diffInSeconds < 60) {
      /** 小于60秒 */
      return `${diffInSeconds < 1 ? 1 : diffInSeconds} s ago`;
    } else if (diffInSeconds < 3600) {
      /** 小于1个小时 */
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes} m ago`;
    } else if (diffInSeconds <= 86400) {
      /** 小于24小时 */
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours} h ago`;
    } else if (diffInSeconds < 604800) {
      /** 小于7天 */
      const days = Math.floor(diffInSeconds / 86400);
      return `${days} days ago`;
    } else {
      /** 大于7天 */
      const date = new Date(timestamp);
      return `${timeUtil.formatDate(date)} ${timeUtil.formatTime(date)}`;
    }
  } catch (error) {
    return '';
  }
}

export function scrollToTop(element, duration = 300) {
  const start = element.scrollTop;
  const change = -start;
  let currentTime = 0;
  const increment = 20; // 每次增加的时间（以毫秒为单位）

  const animateScroll = function () {
    currentTime += increment;
    const val = easeInOutQuad(currentTime, start, change, duration);
    element.scrollTop = val;
    if (currentTime < duration) {
      requestAnimationFrame(animateScroll);
    }
  };

  function easeInOutQuad(t, b, c, d) {
    t /= d / 2;
    if (t < 1) return (c / 2) * t * t + b;
    t--;
    return (-c / 2) * (t * (t - 2) - 1) + b;
  }

  animateScroll();
}

const shareLinkObservables = {};
export function createGetGroupShareLinkObservable(groupId) {
  const cashedObservable = shareLinkObservables[groupId];
  if (cashedObservable) {
    return cashedObservable;
  }
  const observable = defer(() => getShareLink({groupId})).pipe(shareReplay(1));
  shareLinkObservables[groupId] = observable;
  return observable;
}

export function createShareText(content = '') {
  try {
    const originContent = content.length <= 140 ? content : content.slice(0, 140) + '...';
    const safeContent = originContent.replace(/^\n+/, '');
    const text = `#PhemexPulse ${safeContent}`;
    return text;
  } catch (error) {
    return content || '';
  }
}
